import {
    BoxTruckContractorUpdateInput,
    ClientContractor,
    ContractorDirectoryPage,
    ContractorDirectoryQuery,
    ContractorDirectoryTab,
    ContractorStatus,
    DriverRecurringDeductions,
} from '@numeo/types-core'
import { ContractorLog, ContractorLogPageCursor } from '@numeo/types-main-web-app'
import axios from 'axios'
import { getApiBaseUrlWithoutV1 } from '@/setup/SetupAxios'
import { fetchContractorDirectoryPage, fetchContractorsByIds, fetchMatchingContractorIds } from '@/services/contractorDirectoryApi'
import { createScopedRequestCache } from 'utils/scoped-request-cache'
import { showSnackbar, SnackbarType } from 'utils/snackbar'

/**
 * One in-flight Box Truck owner-directory read, shared by every caller asking while it is running — the
 * Vehicles page alone mounts `useContractors` four times.
 *
 * `createScopedRequestCache` owns the two things that make sharing safe: it refuses an answer whose tenant
 * has changed (the company switch happens in-tab), and its generation counter re-issues a read that a save
 * landed on top of, so a post-save `refetch` cannot be answered with the pre-save list.
 *
 * `freshForMs: 0` keeps this a shared request rather than a cache of owners.
 */
const ownerDirectoryRead = async (): Promise<ClientContractor[]> => {
    // `page`/`limit`/`sortDirection` are required by the query type but inert here: this endpoint forwards
    // only search, tab and favourites, and answers with every matching id at once.
    const ids = await fetchMatchingContractorIds(
        { page: 1, limit: 1, search: '', tab: ContractorDirectoryTab.ALL, sortDirection: 'asc', favoriteIds: [] },
        true
    )
    return ids.length > 0 ? fetchContractorsByIds(ids, true) : []
}

const ownerDirectoryCache = createScopedRequestCache<ClientContractor[]>(ownerDirectoryRead, {
    freshForMs: 0,
    retries: 0,
    // Reported where the request lives, and ONCE: the promise is shared, so saying it per waiter is four
    // toasts for one failure on a page that mounts `useContractors` four times. An empty owner list
    // otherwise reads as "this tenant has no owners" rather than as a read that failed.
    onFailure: (error) => {
        console.error('[box-truck] the owner directory could not be read', error)
        showSnackbar(SnackbarType.ERROR, 'Owners could not be loaded. Reload the page to try again.')
    },
})

/** Drop the shared read — the IN-FLIGHT one too, since a read that started before a save answers without
 *  it. Called by `useContractors.refetch`, which every screen uses after saving an owner. */
export const invalidateOwnerDirectory = (): void => ownerDirectoryCache.invalidate()

export interface CreateContractorInput extends Partial<ClientContractor> {
    /** Ask the server to seed a native Box Truck owner profile with authority and direct-deposit fields. */
    createBoxTruckProfile?: Pick<
        BoxTruckContractorUpdateInput,
        // Applicant and tax entries. The Add Owner form renders them, so the create contract has to
        // carry them — rendering a field the payload drops is how "Contractor created successfully"
        // ends with the operator reopening the owner to find the values gone.
        | 'applicantFirstName'
        | 'applicantLastName'
        | 'applicantPhone'
        | 'companyEmergencyPhone'
        | 'companySsn'
        | 'companyTitle'
        | 'companyBirth'
        | 'exemptPayeeCode'
        | 'exemptReportingCode'
        | 'otherText'
        | 'mcNumber'
        | 'usdotNumber'
        | 'doingBusinessAs'
        // The server's buildNativeBoxTruckContractorProfile has always accepted these three; leaving
        // them out of the Pick meant the Company & Authority fields an operator filled in on the
        // create form were dropped at the type boundary, with nothing failing to say so.
        | 'foundationDate'
        | 'companyType'
        | 'limitedLiabilityType'
        | 'depositCompanyName'
        | 'depositBankName'
        | 'depositAccountNumber'
        | 'depositRoutingNumber'
        | 'depositAddress'
        | 'depositStreetAddress'
    >
}

class ContractorApiClient {
    /** One contractor's activity log, newest first. Paged with the last entry shown, like the driver log's. */
    async getContractorLogs(contractorId: string, params?: { limit?: number } & Partial<ContractorLogPageCursor>): Promise<ContractorLog[]> {
        const response = await axios.get<{ data: ContractorLog[] }>(
            `${getApiBaseUrlWithoutV1()}/application/contractors/activity-logs/${encodeURIComponent(contractorId)}/v1`,
            { params }
        )
        return response.data.data || []
    }

    /**
     * @param boxTruckProfiles ask the Profiles mount instead of the generic one.
     *
     * Both paths use the versioned paginated directory contract. The Box Truck mount tells the server this
     * is the Profiles table, where lifecycle and recruiter visibility apply; the generic directory remains
     * complete for TMS consumers. The legacy `/application/contractors` array remains separate.
     */
    async fetchContractorsPage(query: ContractorDirectoryQuery, boxTruckProfiles = false): Promise<ContractorDirectoryPage> {
        return fetchContractorDirectoryPage(query, boxTruckProfiles)
    }

    /** Explicit bulk-selection read: only IDs, across every page matching the current search and tab. */
    async fetchMatchingContractorIds(query: ContractorDirectoryQuery, boxTruckProfiles = false): Promise<string[]> {
        return fetchMatchingContractorIds(query, boxTruckProfiles)
    }

    /**
     * @param scopeKey the TENANT this read belongs to, from `useTenantScope`. It keys the shared
     * in-flight read: the company switch happens in-tab, so a read started on company A and still running
     * when the operator switches must never be handed to company B. Omitted means no sharing at all, which
     * is slower but never wrong.
     */
    async fetchContractors(boxTruckProfiles = false, contractorIds?: string[], scopeKey?: string): Promise<ClientContractor[]> {
        if (boxTruckProfiles) {
            // An EXPLICIT id list is the whole answer, including when it is empty. `[]` used to fall through
            // to the full-directory branch, so creating an owner — no `initialData.id`, and no truck with an
            // owner yet — still read every owner in the tenant, which is the thing this path exists to stop.
            if (contractorIds) return fetchContractorsByIds(contractorIds, true)
            /**
             * Existing truck and asset screens still consume the whole contractor array (they use it as an
             * id lookup), so this still returns all of them — but it ASKS for them in two bounded steps
             * instead of walking the paginated table one page at a time.
             *
             * The walk it replaces issued one sequential request per 100 rows: a tenant with ~3 100 owners
             * paid 31 round trips, every mount, and `MAX_CONTRACTOR_DIRECTORY_OFFSET` meant that past page
             * 101 the walk did not merely get slow, it started failing with "Contractor page is too deep".
             *
             * `contractor-ids` answers "which contractors are visible to me" in ONE request with an ids-only
             * projection, and `fetchContractorsByIds` then hydrates them in batches of 200, four in
             * parallel. Same rows, same Profiles visibility rules (both endpoints sit behind the same
             * guards, so the id set is already filtered), ~5 round trips instead of 31 — and no dependence
             * on the offset ceiling, because every hydrate asks for page 1 by id.
             */
            // Keyed by TENANT, never by a constant. The company switch happens in-tab, so a read started on
            // company A and still running when the operator switches would otherwise be handed to company B
            // and render A's owners on B's page.
            return scopeKey ? ownerDirectoryCache.load(scopeKey) : ownerDirectoryRead()
        }
        const response = await axios.get<ClientContractor[]>('/application/contractors')
        return response.data
    }

    /** @param options.timeoutMs a Box Truck owner create passes one; TMS callers pass nothing and wait as before. */
    async createContractor(contractorData: CreateContractorInput, options?: { timeoutMs?: number }) {
        const response = await axios.post('/application/contractors', contractorData, options?.timeoutMs ? { timeout: options.timeoutMs } : undefined)
        return response.data
    }

    async updateContractor(contractorData: Partial<ClientContractor>): Promise<ClientContractor> {
        const response = await axios.put(`/application/contractors/${contractorData.id}`, contractorData)
        return response.data
    }

    async updateContractorStatus(contractorId: string, status: ContractorStatus): Promise<ClientContractor> {
        const response = await axios.patch<{ success: boolean; data: ClientContractor }>(
            `/box-truck-loads/profiles/contractors/${contractorId}/status/v1`,
            { status },
            { baseURL: getApiBaseUrlWithoutV1() }
        )
        return response.data.data
    }

    async upsertRelayDriverId(contractorId: string, truckId: string, relayDriverId: string): Promise<ClientContractor> {
        const response = await axios.put(`/application/contractors/${contractorId}/relay-driver-id`, { truckId, relayDriverId })
        return response.data
    }

    async deleteContractors(ids: string[]) {
        const response = await axios.delete('/application/contractors', { data: { ids } })
        return response.data
    }

    async updateTruckContract(
        truckId: string,
        contractorId: string,
        payload: { netPayShare: number; grossPercentage?: number; recurringDeductions?: DriverRecurringDeductions }
    ) {
        const response = await axios.put(`/application/trucks/${truckId}/contractors/${contractorId}/contract`, payload)
        return response.data
    }

    async deleteTruckContract(truckId: string, contractorId: string) {
        const response = await axios.delete(`/application/trucks/${truckId}/contractors/${contractorId}/contract`)
        return response.data
    }

    async updateTruckContractorsBulk(
        truckId: string,
        contractors: { contractorId: string; netPayShare: number; grossPercentage?: number; recurringDeductions?: DriverRecurringDeductions }[]
    ) {
        const response = await axios.put(`/application/trucks/${truckId}/contractors`, { contractors })
        return response.data
    }
}

export const contractorApiClient = new ContractorApiClient()
